// /admin/registry: Sites & units for registry administrators (0029).
//
// Clients, sites, units with their work-location classification, work types and each
// site's issuing authority. Every change is a server command recorded with who, when and
// why. Per site the register lists what is still missing before a first permit; nothing
// is filled in for the administrator. Keeping the register gives no signing authority.
import { type ReactNode, useId, useState } from 'react'
import { ErrorBanner } from '@/components/evidence'
import { Badge, Banner, Button, Checkbox, EmptyState, Field, PageHeader, Select, Sheet, Spinner, TextInput } from '@/components/ui'
import type { RegistrySite, RegistryUnit, SiteRegistryView } from '@/domain/registry'
import { useCommand, useMe, useSiteRegistry } from '@/lib/queries'
import { anchorVersionOptions, clientOptions, codeProblem, normaliseCode, optional, siteReadiness, unclassifiedCount } from './model'

type Dialog = 'site' | 'client' | 'work-type' | null

export function RegistryPage() {
  const me = useMe()
  const admin = Boolean(me.data?.global_capabilities?.includes('administer_registry'))
  const q = useSiteRegistry(admin)
  const [dialog, setDialog] = useState<Dialog>(null)
  const [siteId, setSiteId] = useState<string | null>(null)

  if (me.isPending || (admin && q.isPending)) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    )
  }
  if (!admin) {
    return (
      <div className="max-w-3xl">
        <PageHeader title="Sites & units" />
        <Banner tone="info" title="Only a registry administrator can see this page" />
      </div>
    )
  }
  if (!q.data) return <ErrorBanner error={q.error ?? new Error('Sites & units is not available.')} title="Could not load the register" />
  const data = q.data
  const site = data.sites.find((s) => s.id === siteId) ?? null

  return (
    <div className="space-y-5" data-testid="registry-page">
      <PageHeader
        title="Sites & units"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setDialog('site')} data-testid="open-add-site">Add site</Button>
            <Button variant="secondary" onClick={() => setDialog('client')}>Add client</Button>
            <Button variant="secondary" onClick={() => setDialog('work-type')}>Add work type</Button>
          </div>
        }
      />

      {data.sites.length === 0 ? (
        <EmptyState title="No sites yet">Add the project's site, then its units.</EmptyState>
      ) : (
        <Table label="Sites" head={['Code', 'Site', 'Client', 'Units', 'Status', '']}>
          {data.sites.map((s) => {
            const r = siteReadiness(s)
            const open = unclassifiedCount(s)
            return (
              <tr key={s.id} className="border-t border-line align-top" data-testid="site-row">
                <Td className="font-mono">{s.code}</Td>
                <Td className="font-semibold">
                  {s.name}
                  {!s.active ? <span className="block text-[13px] font-normal text-ink-soft">Inactive</span> : null}
                </Td>
                <Td>{s.client?.name ?? <Dash />}</Td>
                <Td>
                  {s.units.length}
                  {open ? <span className="block text-[13px] text-warn">{open} unclassified</span> : null}
                </Td>
                <Td><Badge tone={r.tone}>{r.label}</Badge></Td>
                <Td className="text-right"><Button variant="secondary" onClick={() => setSiteId(s.id)} data-testid="open-site">Manage</Button></Td>
              </tr>
            )
          })}
        </Table>
      )}

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">Work types</h2>
        {data.work_types.length === 0 ? (
          <p className="text-sm text-ink-soft">None yet.</p>
        ) : (
          <Table label="Work types" head={['Code', 'Description', 'Activity group']}>
            {data.work_types.map((w) => (
              <tr key={w.id} className="border-t border-line">
                <Td className="font-mono">{w.code}</Td>
                <Td>{w.description}</Td>
                <Td>{w.activity_group}</Td>
              </tr>
            ))}
          </Table>
        )}
      </section>

      {dialog === 'site' ? <AddSiteSheet view={data} onClose={() => setDialog(null)} onAdded={(id) => { setDialog(null); setSiteId(id) }} /> : null}
      {dialog === 'client' ? <AddClientSheet onClose={() => setDialog(null)} /> : null}
      {dialog === 'work-type' ? <AddWorkTypeSheet view={data} onClose={() => setDialog(null)} /> : null}
      {site ? <SiteSheet view={data} site={site} onClose={() => setSiteId(null)} /> : null}
    </div>
  )
}

function Table({ label, head, children }: { label: string; head: string[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-white">
      <table className="w-full min-w-[560px] border-collapse text-sm" aria-label={label}>
        <thead className="bg-paper text-left">
          <tr>
            {head.map((h, i) => (
              <th key={i} className="px-3 py-2 font-semibold">{h || <span className="sr-only">Actions</span>}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

function Td({ children, className }: { children: ReactNode; className?: string }) {
  return <td className={`px-3 py-2 ${className ?? ''}`}>{children}</td>
}

function Dash() {
  return <span className="text-ink-soft">-</span>
}

function Actions({ busy, label, onCancel }: { busy: boolean; label: string; onCancel: () => void }) {
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      <Button type="submit" loading={busy}>{label}</Button>
      <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
    </div>
  )
}

function Form({ onSubmit, children }: { onSubmit: () => void; children: ReactNode }) {
  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
    >
      {children}
    </form>
  )
}

function AddSiteSheet({ view, onClose, onAdded }: { view: SiteRegistryView; onClose: () => void; onAdded: (id: string) => void }) {
  const cmd = useCommand('register_site')
  const ids = { code: useId(), name: useId(), client: useId() }
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [client, setClient] = useState('')
  const [codeError, setCodeError] = useState<string | null>(null)
  return (
    <Sheet open title="Add site" onClose={onClose}>
      <Form
        onSubmit={async () => {
          const p = codeProblem(code)
          setCodeError(p)
          if (p) return
          const res = await cmd.run({ code: normaliseCode(code), name: name.trim(), client_organisation_id: client || null }).catch(() => null)
          if (res) onAdded(String(res.site_id))
        }}
      >
        <ErrorBanner error={cmd.error} />
        <div className="grid gap-x-4 sm:grid-cols-[10rem_1fr]">
          <Field label="Code" htmlFor={ids.code} error={codeError} required>
            <TextInput id={ids.code} value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" />
          </Field>
          <Field label="Site name" htmlFor={ids.name} required>
            <TextInput id={ids.name} value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
          </Field>
        </div>
        <Field label="Client" htmlFor={ids.client}>
          <Select id={ids.client} value={client} onChange={(e) => setClient(e.target.value)} placeholder="None" options={clientOptions(view)} />
        </Field>
        <Actions busy={cmd.isPending} label="Add site" onCancel={onClose} />
      </Form>
    </Sheet>
  )
}

function AddClientSheet({ onClose }: { onClose: () => void }) {
  const cmd = useCommand('add_organisation')
  const ids = { code: useId(), name: useId(), kind: useId() }
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [kind, setKind] = useState<'client' | 'subcontractor'>('client')
  const [codeError, setCodeError] = useState<string | null>(null)
  return (
    <Sheet open title="Add client or subcontractor" onClose={onClose}>
      <Form
        onSubmit={async () => {
          const p = codeProblem(code)
          setCodeError(p)
          if (p) return
          const res = await cmd.run({ code: normaliseCode(code), name: name.trim(), kind }).catch(() => null)
          if (res) onClose()
        }}
      >
        <ErrorBanner error={cmd.error} />
        <div className="grid gap-x-4 sm:grid-cols-[10rem_1fr]">
          <Field label="Code" htmlFor={ids.code} error={codeError} required>
            <TextInput id={ids.code} value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" />
          </Field>
          <Field label="Name" htmlFor={ids.name} required>
            <TextInput id={ids.name} value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
          </Field>
        </div>
        <Field label="Kind" htmlFor={ids.kind}>
          <Select id={ids.kind} value={kind} onChange={(e) => setKind(e.target.value as 'client' | 'subcontractor')}
            options={[{ value: 'client', label: 'Client' }, { value: 'subcontractor', label: 'Subcontractor' }]} />
        </Field>
        <Actions busy={cmd.isPending} label="Add" onCancel={onClose} />
      </Form>
    </Sheet>
  )
}

function AddWorkTypeSheet({ view, onClose }: { view: SiteRegistryView; onClose: () => void }) {
  const cmd = useCommand('add_work_type')
  const ids = { code: useId(), desc: useId(), group: useId() }
  const [code, setCode] = useState('')
  const [desc, setDesc] = useState('')
  const [group, setGroup] = useState(view.activity_groups[0] ?? '')
  const [codeError, setCodeError] = useState<string | null>(null)
  return (
    <Sheet open title="Add work type" onClose={onClose}>
      <Form
        onSubmit={async () => {
          const p = codeProblem(code)
          setCodeError(p)
          if (p) return
          const res = await cmd.run({ code: normaliseCode(code), description: desc.trim(), activity_group: group }).catch(() => null)
          if (res) onClose()
        }}
      >
        <ErrorBanner error={cmd.error} />
        <div className="grid gap-x-4 sm:grid-cols-[10rem_1fr]">
          <Field label="Code" htmlFor={ids.code} error={codeError} required>
            <TextInput id={ids.code} value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" />
          </Field>
          <Field label="Description" htmlFor={ids.desc} required>
            <TextInput id={ids.desc} value={desc} onChange={(e) => setDesc(e.target.value)} autoComplete="off" />
          </Field>
        </div>
        <Field label="Activity group" htmlFor={ids.group} hint="A work type never sets the permit class.">
          <Select id={ids.group} value={group} onChange={(e) => setGroup(e.target.value)} options={view.activity_groups.map((g) => ({ value: g, label: g }))} />
        </Field>
        <Actions busy={cmd.isPending} label="Add work type" onCancel={onClose} />
      </Form>
    </Sheet>
  )
}

type SiteMode = { kind: 'view' } | { kind: 'unit'; unit: RegistryUnit | null } | { kind: 'edit' } | { kind: 'authority' }

function SiteSheet({ view, site, onClose }: { view: SiteRegistryView; site: RegistrySite; onClose: () => void }) {
  const [mode, setMode] = useState<SiteMode>({ kind: 'view' })
  const back = () => setMode({ kind: 'view' })
  return (
    <Sheet open title={`${site.code} - ${site.name}`} onClose={onClose}>
      {mode.kind === 'unit' ? (
        <UnitForm view={view} site={site} unit={mode.unit} onDone={back} />
      ) : mode.kind === 'edit' ? (
        <EditSiteForm view={view} site={site} onDone={back} />
      ) : mode.kind === 'authority' ? (
        <AuthorityForm view={view} site={site} onDone={back} />
      ) : (
        <div className="space-y-4" data-testid="site-sheet">
          {site.missing.length === 0 ? (
            <Banner tone="ok" title="Ready for permits" />
          ) : (
            <Banner tone="warn" title="Before a first permit">
              <ul className="list-disc pl-5">
                {site.missing.map((m) => <li key={m.code} data-code={m.code}>{m.message}</li>)}
              </ul>
            </Banner>
          )}
          <dl className="grid grid-cols-[9rem_1fr] gap-y-1 text-sm">
            <dt className="text-ink-soft">Client</dt>
            <dd>{site.client?.name ?? 'None'}</dd>
            <dt className="text-ink-soft">Issuing authority</dt>
            <dd>
              {site.authority
                ? `${site.owner ?? 'BadrEOR'}; rulebook ${site.authority.active_version ? `${site.authority.active_version} active` : 'not active yet'}`
                : 'Not set'}
            </dd>
          </dl>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setMode({ kind: 'unit', unit: null })} data-testid="open-add-unit">Add unit</Button>
            <Button variant="secondary" onClick={() => setMode({ kind: 'edit' })}>Edit site</Button>
            {site.authority ? null : <Button variant="secondary" onClick={() => setMode({ kind: 'authority' })}>Set issuing authority</Button>}
          </div>
          {site.units.length === 0 ? null : (
            <Table label="Units" head={['Code', 'Unit', 'Classification', 'Responsible RS', '']}>
              {site.units.map((u) => (
                <tr key={u.id} className="border-t border-line align-top">
                  <Td className="font-mono">{u.code}</Td>
                  <Td>{u.name}</Td>
                  <Td>{u.location_label ?? <span className="text-warn">Not classified</span>}</Td>
                  <Td>{u.responsible_rs ?? <Dash />}</Td>
                  <Td className="text-right"><Button variant="secondary" onClick={() => setMode({ kind: 'unit', unit: u })}>Edit</Button></Td>
                </tr>
              ))}
            </Table>
          )}
        </div>
      )}
    </Sheet>
  )
}

function UnitForm({ view, site, unit, onDone }: { view: SiteRegistryView; site: RegistrySite; unit: RegistryUnit | null; onDone: () => void }) {
  const add = useCommand('register_unit')
  const update = useCommand('update_unit')
  const ids = { code: useId(), name: useId(), loc: useId(), src: useId(), rev: useId(), reason: useId() }
  const [code, setCode] = useState('')
  const [name, setName] = useState(unit?.name ?? '')
  const [loc, setLoc] = useState(unit?.location_classification ?? '')
  const [src, setSrc] = useState(unit?.classification_source ?? '')
  const [rev, setRev] = useState(unit?.classification_revision ?? '')
  const [reason, setReason] = useState('')
  const [codeError, setCodeError] = useState<string | null>(null)
  const changesClass = Boolean(unit) && (loc || null) !== (unit?.location_classification ?? null)
  return (
    <Form
      onSubmit={async () => {
        if (!unit) {
          const p = codeProblem(code)
          setCodeError(p)
          if (p) return
          const res = await add
            .run({ site_id: site.id, code: normaliseCode(code), name: name.trim(), location_classification: loc || null,
                    classification_source: optional(src), classification_revision: optional(rev) })
            .catch(() => null)
          if (res) onDone()
        } else {
          const res = await update
            .run({ site_area_id: unit.id, name: name.trim(), location_classification: loc || null,
                    classification_source: optional(src), classification_revision: optional(rev), reason: reason.trim() })
            .catch(() => null)
          if (res) onDone()
        }
      }}
    >
      <h3 className="mb-2 font-semibold">{unit ? `Edit ${unit.code}` : 'Add unit'}</h3>
      <ErrorBanner error={unit ? update.error : add.error} />
      <div className="grid gap-x-4 sm:grid-cols-[10rem_1fr]">
        {unit ? null : (
          <Field label="Code" htmlFor={ids.code} error={codeError} required>
            <TextInput id={ids.code} value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" />
          </Field>
        )}
        <Field label="Unit name" htmlFor={ids.name} required>
          <TextInput id={ids.name} value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
        </Field>
      </div>
      <Field label="Work-location classification" htmlFor={ids.loc} hint="Leave as not known until it is evidenced; permits wait for it.">
        <Select id={ids.loc} value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="Not known yet"
          options={view.location_classifications.map((c) => ({ value: c.code, label: c.label }))} />
      </Field>
      <div className="grid gap-x-4 sm:grid-cols-[1fr_8rem]">
        <Field label="Evidence (drawing, safety case)" htmlFor={ids.src}>
          <TextInput id={ids.src} value={src} onChange={(e) => setSrc(e.target.value)} />
        </Field>
        <Field label="Revision" htmlFor={ids.rev}>
          <TextInput id={ids.rev} value={rev} onChange={(e) => setRev(e.target.value)} />
        </Field>
      </div>
      {unit ? (
        <Field label="Reason" htmlFor={ids.reason} required hint={changesClass ? 'Changing the classification stops work under permits sealed with the old one.' : undefined}>
          <TextInput id={ids.reason} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      ) : null}
      <Actions busy={add.isPending || update.isPending} label={unit ? 'Save' : 'Add unit'} onCancel={onDone} />
    </Form>
  )
}

function EditSiteForm({ view, site, onDone }: { view: SiteRegistryView; site: RegistrySite; onDone: () => void }) {
  const cmd = useCommand('update_site')
  const ids = { name: useId(), client: useId(), reason: useId() }
  const [name, setName] = useState(site.name)
  const [client, setClient] = useState(site.client?.id ?? '')
  const [active, setActive] = useState(site.active)
  const [reason, setReason] = useState('')
  return (
    <Form
      onSubmit={async () => {
        const res = await cmd
          .run({ site_id: site.id, name: name.trim(), client_organisation_id: client || null, active, reason: reason.trim() })
          .catch(() => null)
        if (res) onDone()
      }}
    >
      <h3 className="mb-2 font-semibold">Edit site</h3>
      <ErrorBanner error={cmd.error} />
      <Field label="Site name" htmlFor={ids.name} required>
        <TextInput id={ids.name} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Client" htmlFor={ids.client}>
        <Select id={ids.client} value={client} onChange={(e) => setClient(e.target.value)} placeholder="None" options={clientOptions(view)} />
      </Field>
      <Checkbox checked={active} onChange={setActive} label="Active" />
      <Field label="Reason" htmlFor={ids.reason} required>
        <TextInput id={ids.reason} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <Actions busy={cmd.isPending} label="Save" onCancel={onDone} />
    </Form>
  )
}

function AuthorityForm({ view, site, onDone }: { view: SiteRegistryView; site: RegistrySite; onDone: () => void }) {
  const cmd = useCommand('set_site_issuing_authority')
  const ids = { version: useId(), ref: useId(), reason: useId() }
  const options = anchorVersionOptions(view)
  const [version, setVersion] = useState(options[0]?.value ?? '')
  const [ref, setRef] = useState('')
  const [reason, setReason] = useState('')
  if (options.length === 0) {
    return (
      <div className="space-y-3">
        <Banner tone="info" title="Needs a rulebook version">Create the rulebook draft on the Rules page first.</Banner>
        <Button variant="secondary" onClick={onDone}>Back</Button>
      </div>
    )
  }
  return (
    <Form
      onSubmit={async () => {
        const res = await cmd
          .run({ site_id: site.id, policy_version_id: version, approval_evidence_reference: optional(ref), reason: reason.trim() })
          .catch(() => null)
        if (res) onDone()
      }}
    >
      <h3 className="mb-2 font-semibold">Issuing authority: {site.owner ?? 'BadrEOR'}</h3>
      <p className="mb-3 text-sm text-ink-soft">Permits here still need an approved rulebook version activated for this site.</p>
      <ErrorBanner error={cmd.error} />
      <Field label="Rulebook version" htmlFor={ids.version} required>
        <Select id={ids.version} value={version} onChange={(e) => setVersion(e.target.value)} options={options} />
      </Field>
      <Field label="Approval reference (optional)" htmlFor={ids.ref}>
        <TextInput id={ids.ref} value={ref} onChange={(e) => setRef(e.target.value)} />
      </Field>
      <Field label="Reason" htmlFor={ids.reason} required>
        <TextInput id={ids.reason} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <Actions busy={cmd.isPending} label="Set" onCancel={onDone} />
    </Form>
  )
}
